import type { UserProfile } from '@/lib/auth/client';
import type { IntercomIdentityDto } from '@/lib/models';
import type { IntercomBootSettings, IntercomController } from './intercom-controller';

/**
 * Map the auth profile to Intercom display attributes. These are NOT part of
 * the Identity-Verification hash (only `user_id` is), so they're safe to read
 * from the local profile. null/empty values are dropped so we never overwrite
 * a real Intercom attribute with a blank.
 */
export function toIntercomUser(user: UserProfile): IntercomBootSettings {
  const name = user.displayName || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.nickname || undefined;

  const createdAtMs = user.createdAt ? new Date(user.createdAt).getTime() : NaN;

  return {
    ...(name ? { name } : {}),
    ...(user.email ? { email: user.email } : {}),
    ...(Number.isFinite(createdAtMs) ? { created_at: Math.floor(createdAtMs / 1000) } : {}),
  };
}

interface ReconcileInput {
  /** Current auth profile, or null when logged out. */
  user: UserProfile | null;
  /** Server identity (user_id + IV hash) for the current player, or null until it loads. */
  identity: IntercomIdentityDto | null;
}

// Module-level transition state — mirrors analytics' reconcileIdentity. `booted`
// guards the first boot (anonymous), `currentUserId` makes re-runs idempotent.
const state: { booted: boolean; currentUserId: string | null } = { booted: false, currentUserId: null };

/**
 * Drive Intercom boot/identify off auth + server-identity state. Idempotent:
 * re-running with the same user is a no-op. We only identify once the server
 * identity has loaded (so the messenger boots with a matching `user_hash` and
 * Identity Verification passes); until then the user is treated as anonymous.
 *
 * Transitions (Intercom requires shutdown + re-boot to switch sessions, so the
 * previous user's data never bleeds into the next):
 *   first run            boot (anonymous, or identified if identity is ready)
 *   anon → identified    shutdown + boot(user)
 *   userA → userB        shutdown + boot(B)
 *   identified → logout  shutdown + boot(anonymous)
 */
export function reconcileIntercom(controller: IntercomController, input: ReconcileInput): void {
  const { user, identity } = input;
  const nextUserId = user && identity ? identity.userId : null;

  if (state.booted && nextUserId === state.currentUserId) return;

  if (state.booted) {
    controller.shutdown();
  }

  if (user && identity) {
    controller.boot({
      ...toIntercomUser(user),
      user_id: identity.userId,
      ...(identity.userHash ? { user_hash: identity.userHash } : {}),
    });
  } else {
    controller.boot({});
  }

  state.booted = true;
  state.currentUserId = nextUserId;
}

/** Test-only escape hatch. Internal to the intercom surface. */
export function __resetIntercomStateForTests(): void {
  state.booted = false;
  state.currentUserId = null;
}
